import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminAdPreview, adminAdPreviewVariants } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const INK = "#1A1A2E";
const MUTED = "#5b5e69";
const BG = "#F3F4F6";

// A visual mock of the Meta feed ad card, styled to match Facebook/Instagram
// enough that the owner can spot bad copy before spending money.
function MetaFeedMock({ creative, businessName }) {
  const displayName = businessName?.trim() || "NearNow";
  return (
    <div
      data-testid="meta-feed-mock"
      style={{
        background: "#fff",
        border: "1px solid #dfe1e5",
        borderRadius: 8,
        overflow: "hidden",
        maxWidth: 500,
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        color: "#050505",
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12 }}>
        <div
          style={{
            width: 40, height: 40, borderRadius: 999,
            background: NAVY, color: GOLD,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 800, fontSize: 16,
          }}
        >
          NN
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#050505" }}>
            {displayName}
            <span
              style={{ marginLeft: 6, fontSize: 11, color: "#65676b", fontWeight: 500 }}
            >
              · Sponsored · <span aria-hidden>🌐</span>
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#65676b" }}>Rhode Island</div>
        </div>
        <div style={{ color: "#65676b", fontSize: 20, lineHeight: 1 }}>⋯</div>
      </div>

      {/* Primary text */}
      <div style={{ padding: "0 12px 12px", fontSize: 14, lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
        {creative.primary_text}
      </div>

      {/* Image placeholder (real creative uploaded separately in Ads Manager) */}
      <div
        style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #142a4a 100%)`,
          minHeight: 260,
          position: "relative",
          borderTop: "1px solid #dfe1e5",
          borderBottom: "1px solid #dfe1e5",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <div
          style={{
            color: GOLD,
            fontFamily: "'Bebas Neue', sans-serif",
            letterSpacing: 3,
            fontSize: 26,
            textAlign: "center",
            padding: 20,
          }}
        >
          {creative.headline.toUpperCase()}
        </div>
        <div
          style={{
            position: "absolute", top: 10, right: 10,
            fontSize: 10, color: "rgba(255,255,255,0.55)",
            border: "1px dashed rgba(255,255,255,0.35)", padding: "2px 6px",
            borderRadius: 3,
          }}
        >
          UPLOAD CREATIVE IN ADS MANAGER
        </div>
      </div>

      {/* Link card */}
      <div
        style={{
          display: "flex", alignItems: "center",
          padding: 12, gap: 12, background: "#f0f2f5",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, color: "#65676b", textTransform: "uppercase" }}>
            {creative.display_url}
          </div>
          <div style={{ fontWeight: 700, fontSize: 16, color: "#050505", lineHeight: 1.2 }}>
            {creative.headline}
          </div>
          <div style={{ fontSize: 13, color: "#65676b", marginTop: 2 }}>
            {creative.description}
          </div>
        </div>
        <button
          style={{
            background: "#e4e6eb",
            border: "none",
            padding: "8px 14px",
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 13,
            color: "#050505",
            cursor: "default",
          }}
        >
          {(creative.cta_type || "SIGN_UP").replace("_", " ")}
        </button>
      </div>
    </div>
  );
}

const label = { fontSize: 12, color: NAVY, letterSpacing: 1, fontFamily: "'Bebas Neue', sans-serif", marginBottom: 6, display: "block" };
const input = { width: "100%", padding: "12px 14px", fontSize: 15, border: `1.5px solid rgba(10,22,40,0.15)`, borderRadius: 6, background: "#fff", color: INK, minHeight: 44, boxSizing: "border-box" };

export default function AdPreviewPage() {
  const [variants, setVariants] = useState([]);
  const [variant, setVariant] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [city, setCity] = useState("Providence");
  const [trade, setTrade] = useState("plumbing");
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    adminAdPreviewVariants()
      .then((r) => {
        const list = r.variants || [];
        setVariants(list);
        if (list[0]) setVariant(list[0].key);
      })
      .catch((e) => setErr(e?.response?.data?.detail || e.message));
  }, []);

  useEffect(() => {
    if (!variant) return;
    let cancelled = false;
    setLoading(true);
    setErr("");
    adminAdPreview({ variant, business_name: businessName, city, trade })
      .then((r) => {
        if (cancelled) return;
        if (r.ok === false) {
          setErr(r.error || "Preview failed.");
          setPreview(null);
        } else {
          setPreview(r);
        }
      })
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [variant, businessName, city, trade]);

  const active = useMemo(
    () => variants.find((v) => v.key === variant),
    [variants, variant]
  );

  return (
    <div data-testid="ad-preview-page" style={{ padding: "28px 22px", maxWidth: 1200, margin: "0 auto" }}>
      <Link
        to="/admin"
        style={{ fontSize: 13, color: NAVY, textDecoration: "underline" }}
        data-testid="back-to-admin"
      >
        ← ADMIN
      </Link>
      <h1
        className="font-display"
        style={{ color: NAVY, fontSize: 44, margin: "8px 0 6px", lineHeight: 1 }}
      >
        META AD PREVIEW
      </h1>
      <p style={{ color: MUTED, fontSize: 16, marginTop: 0, maxWidth: 720 }}>
        The exact creative + headline + description that will hit Meta Ads
        Manager for each of our audience variants. Change the business name,
        city, or trade to eyeball the ad in context before spending a dollar.
      </p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0,340px) 1fr",
          gap: 32,
          alignItems: "start",
          marginTop: 24,
        }}
      >
        {/* Controls */}
        <div
          style={{
            background: "#fff",
            border: "1px solid rgba(10,22,40,0.10)",
            borderRadius: 10,
            padding: 22,
            position: "sticky",
            top: 20,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <label style={label}>VARIANT</label>
            <select
              value={variant}
              onChange={(e) => setVariant(e.target.value)}
              data-testid="variant-select"
              style={input}
            >
              {variants.map((v) => (
                <option key={v.key} value={v.key}>
                  {v.headline}
                </option>
              ))}
            </select>
            {active && (
              <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>
                <strong>Audience:</strong> {active.audience}
              </div>
            )}
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={label}>BUSINESS NAME (optional)</label>
            <input
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="e.g. Ajakaiye Plumbing"
              data-testid="business-name-input"
              style={input}
            />
            <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
              Prepends "Hey {"{Business}"}—" to contractor variants.
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={label}>CITY</label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                data-testid="city-input"
                style={input}
              />
            </div>
            <div>
              <label style={label}>TRADE</label>
              <input
                value={trade}
                onChange={(e) => setTrade(e.target.value)}
                data-testid="trade-input"
                style={input}
              />
            </div>
          </div>

          {loading && (
            <div style={{ fontSize: 13, color: MUTED }}>Rendering preview…</div>
          )}
          {err && (
            <div
              data-testid="ad-preview-error"
              style={{
                background: "#FDECEA", color: "#B4241E",
                border: "1px solid #B4241E", padding: 10, borderRadius: 6,
                fontSize: 13,
              }}
            >
              {err}
            </div>
          )}
        </div>

        {/* Preview + spec */}
        <div>
          {preview && preview.creative ? (
            <>
              <div style={{ marginBottom: 22 }}>
                <MetaFeedMock creative={preview.creative} businessName={businessName} />
              </div>

              <div
                data-testid="ad-preview-spec"
                style={{
                  background: NAVY, color: "#EDEFF2",
                  borderRadius: 10, padding: 22, fontSize: 14,
                  lineHeight: 1.55,
                }}
              >
                <div className="font-display" style={{ color: GOLD, letterSpacing: 2, fontSize: 16, marginBottom: 10 }}>
                  CAMPAIGN SPEC
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: "6px 14px" }}>
                  <Spec k="Objective" v={preview.creative.cta_type === "GET_QUOTE" ? "OUTCOME_LEADS · GET_QUOTE" : `OUTCOME_LEADS · ${preview.creative.cta_type}`} />
                  <Spec k="Destination" v={preview.creative.destination_url} link />
                  <Spec k="Placements" v={(preview.targeting.publisher_platforms || []).join(", ") + " · mobile"} />
                  <Spec k="Age" v={`${preview.targeting.age_min}–${preview.targeting.age_max}`} />
                  <Spec k="Daily budget" v={`$${preview.budget.daily_budget_usd}`} />
                  <Spec k="Lifetime cap" v={`$${preview.budget.lifetime_cap_usd}`} />
                  <Spec k="Pixel" v={preview.pixel.pixel_id} />
                  <Spec k="Conversion event" v={preview.pixel.conversion_event} />
                </div>
                <div style={{ color: GOLD, fontSize: 12, marginTop: 14 }}>
                  💡 {preview.budget.note}
                </div>
              </div>
            </>
          ) : !loading && !err ? (
            <div style={{ color: MUTED }}>Pick a variant to preview.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const Spec = ({ k, v, link }) => (
  <>
    <div style={{ color: "#9AA5B4", textTransform: "uppercase", fontSize: 11, letterSpacing: 1, paddingTop: 2 }}>{k}</div>
    <div style={{ wordBreak: "break-all" }}>
      {link ? (
        <a href={v} target="_blank" rel="noreferrer" style={{ color: "#EDEFF2", textDecoration: "underline" }}>{v}</a>
      ) : v}
    </div>
  </>
);
